<template>
  <div class="field-service-form">
    <el-form-item label="Client Name" prop="clientName">
      <el-input
        :model-value="modelValue.clientName"
        size="large"
        placeholder="Enter Client Name"
        class="w-[400px]"
        :disabled="disabled"
        @input="(val: string) => emit('update:field', 'clientName', val)"
      />
    </el-form-item>

    <el-form-item label="Location" prop="location">
      <el-input
        :model-value="modelValue.location"
        size="large"
        class="w-[400px]"
        placeholder="Enter Location"
        :disabled="disabled"
        @input="(val: string) => emit('update:field', 'location', val)"
      />
    </el-form-item>

    <el-form-item label="Manufacturer" prop="manufacturer">
      <el-input
        :model-value="modelValue.manufacturer"
        size="large"
        class="w-[400px]"
        placeholder="Enter Manufacturer"
        :disabled="disabled"
        @input="(val: string) => emit('update:field', 'manufacturer', val)"
      />
    </el-form-item>

    <el-form-item label="Model" prop="model">
      <el-input
        :model-value="modelValue.model"
        size="large"
        class="w-[400px]"
        placeholder="Enter Model"
        :disabled="disabled"
        @input="(val: string) => emit('update:field', 'model', val)"
      />
    </el-form-item>

    <el-form-item label="Serial No" prop="serialNo">
      <el-input
        :model-value="modelValue.serialNo"
        size="large"
        class="w-[400px]"
        placeholder="Enter Serial No"
        :disabled="disabled"
        @input="(val: string) => emit('update:field', 'serialNo', val)"
      />
    </el-form-item>

    <!-- SMU Hours -->
    <el-form-item label="SMU Hours" prop="SMUHour">
      <div class="flex items-center">
        <img
          src="@/assets/icon_clock.svg"
          height="16"
          width="16"
          class="el-icon-clock mr-2"
        />
        <el-input-number
          :model-value="modelValue.SMUHour"
          :controls="false"
          size="large"
          class="w-[150px]"
          :disabled="disabled"
          @change="(val: any) => emit('update:field', 'SMUHour', val || 0)"
        />
      </div>
    </el-form-item>

    <!-- Vehicle Reg No -->
    <el-form-item label="Veh. Reg. No" prop="vehRegNo">
      <el-input
        :model-value="modelValue.vehRegNo"
        size="large"
        class="w-[400px]"
        placeholder="Enter Veh. Reg. No"
        :disabled="disabled"
        @input="(val: string) => emit('update:field', 'vehRegNo', val)"
      />
    </el-form-item>

    <!-- Travel KMs -->
    <el-form-item label="Travel KMs" prop="travelKMS">
      <div class="flex items-center">
        <img
          src="@/assets/icon_clock.svg"
          height="16"
          width="16"
          class="el-icon-clock mr-2"
        />
        <el-input-number
          :model-value="modelValue.travelKMS"
          :controls="false"
          size="large"
          class="w-[150px]"
          :disabled="disabled"
          @change="(val: any) => emit('update:field', 'travelKMS', val || 0)"
        />
      </div>
    </el-form-item>

    <!-- Field Service Notes -->
    <el-form-item label="Field Service Notes" prop="fieldServiceNote">
      <el-input
        :model-value="modelValue.fieldServiceNote"
        :rows="3"
        type="textarea"
        placeholder="Enter Notes"
        size="large"
        class="w-[400px]"
        :disabled="disabled"
        @input="(val: string) => emit('update:field', 'fieldServiceNote', val)"
      />
    </el-form-item>

    <!-- Customer Email -->
    <el-form-item label="Customer Email" prop="customerEmail">
      <el-input
        :model-value="modelValue.customerEmail"
        size="large"
        class="w-[400px]"
        placeholder="Enter Customer Email"
        :disabled="disabled"
        @input="(val: string) => emit('update:field', 'customerEmail', val)"
      />
    </el-form-item>

    <!-- Customer Acceptance Name -->
    <el-form-item 
      label="Customer Acceptance Name" 
      prop="customerAcceptanceName"
      class="customer-acceptance-name"
    >
      <el-input
        :model-value="modelValue.customerAcceptanceName"
        size="large"
        placeholder="Enter Customer Acceptance Name"
        :disabled="disabled"
        @input="(val: string) => emit('update:field', 'customerAcceptanceName', val)"
      />
    </el-form-item>

    <!-- Customer Acceptance Signature -->
    <el-form-item class="customer-signature" label="Customer Acceptance" prop="isCustomerAcceptance">
      <Vue3Signature
        ref="signatureCustomer"
        :sigOption="sigOption"
        :h="'200px'"
        :w="'400px'"
        :disabled="disabled"
        class="sign-pad"
        :class="{ 'disabled-signature': disabled }"
        @mousedown="(e: any) => !disabled && handleSignatureClick(e, 'customer')"
      />
      <div v-if="!disabled" class="w-full flex justify-end mt-[8px]">
        <el-button
          class="btn-cancel"
          @click="() => handleClearSignature('customer')"
        >
          Clear
        </el-button>
      </div>
    </el-form-item>

    <!-- Mechanical Acceptance Signature -->
    <el-form-item class="mechanical-signature" label="Mechanical Acceptance" prop="isMechanicalAcceptance">
      <Vue3Signature
        ref="signatureMechanical"
        :sigOption="sigOption"
        :h="'200px'"
        :w="'400px'"
        :disabled="disabled"
        class="sign-pad"
        :class="{ 'disabled-signature': disabled }"
        @mousedown="(e: any) => !disabled && handleSignatureClick(e, 'mechanical')"
      />
      <div v-if="!disabled" class="w-full flex justify-end mt-[8px]">
        <el-button
          class="btn-cancel"
          @click="() => handleClearSignature('mechanical')"
        >
          Clear
        </el-button>
      </div>
    </el-form-item>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, nextTick } from "vue";
import type { FieldServiceInfo } from "@/composables/v2/useFieldService";
import { imageEndpoint } from "@/config/enviroments";

interface Props {
  modelValue: FieldServiceInfo;
  sigOption: { penColor: string; backgroundColor: string };
  disabled?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:field', field: keyof FieldServiceInfo, value: any): void;
  (e: 'signature-click', type: 'customer' | 'mechanical'): void;
  (e: 'signature-clear', type: 'customer' | 'mechanical'): void;
}>();

const signatureCustomer = ref();
const signatureMechanical = ref();
const signaturesLoaded = ref(false);

// Helper to convert URL to base64
const getBase64FromUrl = async (url: string): Promise<string> => {
  const data = await fetch(url);
  const blob = await data.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
  });
};

// Load existing signatures when in edit mode
const loadExistingSignatures = async () => {
  if (signaturesLoaded.value) return;
  
  await nextTick(); // Wait for refs to be ready
  
  const mechanical = props.modelValue?.mechanicalAcceptance;
  const customer = props.modelValue?.customerAcceptance;
  
  // Load mechanical signature
  if (mechanical && signatureMechanical.value?.fromDataURL) {
    try {
      // If already base64 (length > 200), use directly; otherwise load from URL
      const base64 = mechanical.length > 200 
        ? mechanical 
        : await getBase64FromUrl(`${imageEndpoint}/${mechanical}`);
      signatureMechanical.value.fromDataURL(base64);
    } catch (err) {
      // Error loading signature
    }
  }
  
  // Load customer signature
  if (customer && signatureCustomer.value?.fromDataURL) {
    try {
      const base64 = customer.length > 200 
        ? customer 
        : await getBase64FromUrl(`${imageEndpoint}/${customer}`);
      signatureCustomer.value.fromDataURL(base64);
    } catch (err) {
      // Error loading signature
    }
  }
  
  signaturesLoaded.value = true;
};

// Watch for signature data changes to load existing signatures
watch(() => [props.modelValue?.mechanicalAcceptance, props.modelValue?.customerAcceptance], 
  ([mechanical, customer]) => {
    if ((mechanical || customer) && !signaturesLoaded.value) {
      loadExistingSignatures();
    }
  }, 
  { immediate: true }
);

// Also try loading on mount (in case data is already available)
onMounted(() => {
  if (props.modelValue?.mechanicalAcceptance || props.modelValue?.customerAcceptance) {
    loadExistingSignatures();
  }
});

const handleSignatureClick = (event: any, type: 'customer' | 'mechanical') => {
  if (type === 'customer') {
    emit('update:field', 'isCustomerAcceptance', 'change');
  } else {
    emit('update:field', 'isMechanicalAcceptance', 'change');
  }
  emit('signature-click', type);
};

const handleClearSignature = (type: 'customer' | 'mechanical') => {
  if (type === 'customer') {
    emit('update:field', 'customerAcceptance', '');
    emit('update:field', 'isCustomerAcceptance', '');
    if (signatureCustomer.value?.clear) signatureCustomer.value.clear();
  } else {
    emit('update:field', 'mechanicalAcceptance', '');
    emit('update:field', 'isMechanicalAcceptance', '');
    if (signatureMechanical.value?.clear) signatureMechanical.value.clear();
  }
  emit('signature-clear', type);
};

// Expose signature refs for parent access
defineExpose({
  signatureCustomer,
  signatureMechanical,
});
</script>

<style lang="scss" scoped>
.field-service-form {
  .customer-acceptance-name {
    :deep(label) {
      line-height: 20px !important;
    }
  }

  .customer-signature,
  .mechanical-signature {
    :deep(.el-form-item__label) {
      line-height: 20px !important;
    }
  }

  .sign-pad {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
  }

  :deep(.is-error) {
    .sign-pad {
      border-color: #f56c6c !important;
    }
  }
}
</style>
